import React from 'react';
import { useTranslation } from 'react-i18next';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import FormatAlignLeftIcon from '@material-ui/icons/FormatAlignLeft';
import FormatAlignCenterIcon from '@material-ui/icons/FormatAlignCenter';
import FormatAlignRightIcon from '@material-ui/icons/FormatAlignRight';
import IconButton from '@material-ui/core/IconButton';
import { Clear } from '@material-ui/icons';
import { Divider, OutlinedInput } from '@material-ui/core';

import ColorPicker from '../../../../../../../../../../reusable/ColorPicker/ColorPicker';
import CustomSlider from '../../../../../../../../../../reusable/Slider/CustomSlider';
import CardData from '../../../../../../../../utils/CardData';
import { useCustomStyles } from "../../../../../utils";

import TextStyling from './TextStyling';
import { WidgetProps } from '../../../../../../../widgetOverlay/widgetInteractionItem/WidgetProps';
import { getWidgetProps } from '../../../../../../../../utils/helper';

interface TextProps {
	widgetTemplate: WidgetProps.WidgetTemplate;
	isAtHoverSetting: boolean;
	setHoverSetting: (_value: boolean) => {};
	handleStylingAttributeChange: (_objKey: string | null, _attrKey: string, _value: any) => {};
}

const SLIDER_OPTION_MARKS = [
	{ value: 0, label: '0px' },
	{ value: 20 },
	{ value: 40 },
	{ value: 60 },
	{ value: 80 },
	{ value: 100 },
	{ value: 120 },
	{ value: 140 },
	{ value: 160 },
	{ value: 180 },
	{ value: 200, label: '200px' },
];

const Text = ({ widgetTemplate: widgetTemplateProp, handleStylingAttributeChange }: TextProps) => {
	const { t: translator } = useTranslation();
	const classes = useCustomStyles();

	const widgetTemplate = getWidgetProps(widgetTemplateProp) as WidgetProps.WidgetTemplate;
	const {
		style: {
			textAlignment,
			contentSpacing,
			headerLabel: {
				text: headerText,
				font: headerFont,
				fontSize: headerFontSize,
				fontWeight: headerFontWeight,
				lineHeight: headerLineHeight,
				color: headerColor,
				shadowColor: headerShadowColor,
			} = {},
			textLabel: {
				text: paragraphText,
				font: paragraphFont,
				fontSize: paragraphFontSize,
				fontWeight: paragraphFontWeight,
				lineHeight: paragraphLineHeight,
				color: paragraphColor,
				shadowColor: paragraphShadowColor,
			} = {},
		},
	} = widgetTemplate;

	return (
		<CardData title={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_CONTENT_LABEL')}>
			<div className="widget-props-wrapper">
				<FormControlLabel
					className={'scenario-chart__control scenario-chart__control--fullWidth'}
					label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_CONTENT_HEADER_LABEL')}
					onChange={({ target }) => {
						handleStylingAttributeChange('headerLabel', 'text', (target as HTMLInputElement).value);
					}}
					labelPlacement="top"
					control={<OutlinedInput multiline minRows={1} value={headerText} className={classes.multipleLinesInput} />}
				/>
				<FormControlLabel
					className={'scenario-chart__control'}
					label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_CONTENT_ALIGNMENT')}
					labelPlacement="start"
					control={
						<ToggleButtonGroup className="button-icon-configure-container">
							<ToggleButton
								value={'left'}
								aria-label="left aligned"
								size="small"
								className="toggle-button-custom"
								onClick={() => {
									handleStylingAttributeChange(null, 'textAlignment', 'left');
								}}
								selected={textAlignment === 'left'}
							>
								<FormatAlignLeftIcon />
							</ToggleButton>
							<ToggleButton
								value={'center'}
								aria-label="center aligned"
								size="small"
								className="toggle-button-custom"
								onClick={() => {
									handleStylingAttributeChange(null, 'textAlignment', 'center');
								}}
								selected={textAlignment === 'center'}
							>
								<FormatAlignCenterIcon />
							</ToggleButton>
							<ToggleButton
								value={'right'}
								aria-label="right aligned"
								size="small"
								className="toggle-button-custom"
								onClick={() => {
									handleStylingAttributeChange(null, 'textAlignment', 'right');
								}}
								selected={textAlignment === 'right'}
							>
								<FormatAlignRightIcon />
							</ToggleButton>
						</ToggleButtonGroup>
					}
				/>
				<TextStyling
					id="header"
					font={headerFont}
					fontSize={headerFontSize as any}
					fontWeight={headerFontWeight as any}
					lineHeight={headerLineHeight as any}
					handleStylingAttributeChange={handleStylingAttributeChange}
				/>
				<FormControlLabel
					className={'scenario-chart__control'}
					label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_STYLING_TEXT_COLOR')}
					labelPlacement="start"
					control={
						<div className="scene-widgets__sort-input-control">
							<OutlinedInput value={headerColor ?? ""} className={classes.input} />
							<ColorPicker
								width="28px"
								height="28px"
								padding="4px"
								color={headerColor ?? ""}
								onChangeComplete={(color: any) => {
									handleStylingAttributeChange('headerLabel', 'color', color.hex);
								}}
							/>
							<IconButton
								size="small"
								onClick={() => handleStylingAttributeChange('headerLabel', 'color', '')}
							>
								<Clear />
							</IconButton>
						</div>
					}
				/>
				<FormControlLabel
					className={'scenario-chart__control'}
					label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_STYLING_TEXT_SHADOW')}
					labelPlacement="start"
					control={
						<div className="scene-widgets__sort-input-control">
							<OutlinedInput value={headerShadowColor ?? ""} className={classes.input} />
							<ColorPicker
								width="28px"
								height="28px"
								padding="4px"
								color={headerShadowColor ?? ""}
								onChangeComplete={(color: any) => {
									handleStylingAttributeChange('headerLabel', 'shadowColor', color.hex);
								}}
							/>
							<IconButton
								size="small"
								onClick={() => handleStylingAttributeChange('headerLabel', 'shadowColor', 'transparent')}
							>
								<Clear />
							</IconButton>
						</div>
					}
				/>

				<Divider className="divider-extra-space" />
				<FormControlLabel
					className={'scenario-chart__control scenario-chart__control--fullWidth'}
					label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_CONTENT_PARAGRAPH_LABEL')}
					onChange={({ target }) => {
						handleStylingAttributeChange('textLabel', 'text', (target as HTMLInputElement).value);
					}}
					labelPlacement="top"
					control={<OutlinedInput multiline minRows={8} value={paragraphText ?? ""} className={classes.multipleLinesInput} />}
				/>
				<FormControlLabel
					className={'scenario-chart__control'}
					label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_CONTENT_ALIGNMENT')}
					labelPlacement="start"
					control={
						<ToggleButtonGroup className="button-icon-configure-container">
							<ToggleButton
								value={'left'}
								aria-label="left aligned"
								size="small"
								className="toggle-button-custom"
								onClick={() => {
									handleStylingAttributeChange(null, 'textAlignment', 'left');
								}}
								selected={textAlignment === 'left'}
							>
								<FormatAlignLeftIcon />
							</ToggleButton>
							<ToggleButton
								value={'center'}
								aria-label="left aligned"
								size="small"
								className="toggle-button-custom"
								onClick={() => {
									handleStylingAttributeChange(null, 'textAlignment', 'center');
								}}
								selected={textAlignment === 'center'}
							>
								<FormatAlignCenterIcon />
							</ToggleButton>
							<ToggleButton
								value={'right'}
								aria-label="right aligned"
								size="small"
								className="toggle-button-custom"
								onClick={() => {
									handleStylingAttributeChange(null, 'textAlignment', 'right');
								}}
								selected={textAlignment === 'right'}
							>
								<FormatAlignRightIcon />
							</ToggleButton>
						</ToggleButtonGroup>
					}
				/>
				<TextStyling
					id="text"
					font={paragraphFont}
					fontSize={paragraphFontSize as any}
					fontWeight={paragraphFontWeight as any}
					lineHeight={paragraphLineHeight as any}
					handleStylingAttributeChange={handleStylingAttributeChange}
				/>
				<FormControlLabel
					className={'scenario-chart__control'}
					label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_STYLING_TEXT_COLOR')}
					labelPlacement="start"
					control={
						<div className="scene-widgets__sort-input-control">
							<OutlinedInput value={paragraphColor ?? ""} className={classes.input} />
							<ColorPicker
								width="28px"
								height="28px"
								padding="4px"
								color={paragraphColor ?? ""}
								onChangeComplete={(color: any) => {
									handleStylingAttributeChange('textLabel', 'color', color.hex);
								}}
							/>
							<IconButton
								size="small"
								onClick={() => handleStylingAttributeChange('textLabel', 'color', '')}
							>
								<Clear />
							</IconButton>
						</div>
					}
				/>
				<FormControlLabel
					className={'scenario-chart__control'}
					label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_STYLING_TEXT_SHADOW')}
					labelPlacement="start"
					control={
						<div className="scene-widgets__sort-input-control">
							<OutlinedInput value={paragraphShadowColor ?? ""} className={classes.input} />
							<ColorPicker
								width="28px"
								height="28px"
								padding="4px"
								color={paragraphShadowColor ?? ""}
								onChangeComplete={(color: any) => {
									handleStylingAttributeChange('textLabel', 'shadowColor', color.hex);
								}}
							/>
							<IconButton
								size="small"
								onClick={() => handleStylingAttributeChange('textLabel', 'shadowColor', 'transparent')}
							>
								<Clear />
							</IconButton>
						</div>
					}
				/>

				<Divider className="divider-extra-space" />
				<FormControlLabel
					className={'scenario-chart__control'}
					label={translator('SCENARIO_SCENE_EDITOR_WIDGET_SETTING_TEXT_WIDGET_STYLING_CONTENT_SPACING')}
					labelPlacement="start"
					control={
						<CustomSlider
							value={contentSpacing}
							min={0}
							max={200}
							marks={SLIDER_OPTION_MARKS}
							onChange={(e: any, value: number | number[]) => {
								handleStylingAttributeChange(null, 'contentSpacing', value);
							}}
						/>
					}
				/>
			</div>
		</CardData>
	);
};

export default Text;
